// Activities import — header normalization, fuzzy mapping, row parsing,
// and bulk insert. Mirrors the POB import pattern.
//
// Future: WellView/WITSML API integration
// WellView exports daily activity sequences via WITSML 1.3.1/1.4.1 RESTful
// API. CSV/Excel import is the manual fallback until operator has WellView
// API configured. Target: Walk/Run phase integration.

import { supabase } from './supabase';

async function getAuthUserId(): Promise<string> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.user?.id) throw new Error('Not authenticated');
  return session.user.id;
}

async function getTenantId(): Promise<string> {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) throw new Error('Not authenticated');
  const payload = JSON.parse(
    atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')),
  );
  const tenantId = payload.tenant_id as string | undefined;
  if (!tenantId) throw new Error('Tenant not resolved');
  return tenantId;
}

export type ActivityField =
  | 'description'
  | 'status'
  | 'risk_level'
  | 'risk_factor_category'
  | 'risk_tags'
  | 'start_time'
  | 'duration_minutes'
  | 'crew_size'
  | 'requires_jsa'
  | 'is_complete'
  | 'completion_notes';

export const REQUIRED_FIELDS: ActivityField[] = ['description'];

export const ALL_FIELDS: { key: ActivityField; label: string; required: boolean }[] = [
  { key: 'description', label: 'Description', required: true },
  { key: 'status', label: 'Status', required: false },
  { key: 'risk_level', label: 'Risk Level', required: false },
  { key: 'risk_factor_category', label: 'Risk Factor Category', required: false },
  { key: 'risk_tags', label: 'Risk Tags', required: false },
  { key: 'start_time', label: 'Start Time (HH:MM)', required: false },
  { key: 'duration_minutes', label: 'Duration (min)', required: false },
  { key: 'crew_size', label: 'Crew Size', required: false },
  { key: 'requires_jsa', label: 'JSA Required', required: false },
  { key: 'is_complete', label: 'Complete', required: false },
  { key: 'completion_notes', label: 'Completion Notes', required: false },
];

const VALID_STATUSES = ['planned', 'in_progress', 'completed', 'cancelled', 'deferred'] as const;
type ActivityStatus = (typeof VALID_STATUSES)[number];
const VALID_RISK = ['low', 'medium', 'high'] as const;
type ActivityRiskLevel = (typeof VALID_RISK)[number];
const VALID_CATEGORIES = ['personnel', 'equipment', 'environment', 'process', 'organizational', 'external'] as const;

export function normalizeHeader(h: string): string {
  return h.trim().toLowerCase().replace(/[\s\-]+/g, '_');
}

export function hasRequiredMozaicColumns(cols: string[]): boolean {
  const set = new Set(cols.map(normalizeHeader));
  return REQUIRED_FIELDS.every((r) => set.has(r));
}

export function detectColumnMapping(cols: string[]): Record<string, ActivityField> {
  const out: Record<string, ActivityField> = {};
  const taken = new Set<ActivityField>();

  const patterns: [ActivityField, RegExp[]][] = [
    ['description', [/^description$/, /\bactivity\b/, /work.?description/, /^task$/, /\bdetails?\b/]],
    ['status', [/^status$/, /\bstate\b/]],
    ['risk_level', [/risk.?level/, /risk.?rating/, /^risk$/]],
    ['risk_factor_category', [/risk.?(factor.?)?categor/, /^category$/, /^categories$/]],
    ['risk_tags', [/risk.?tags?/, /^tags?$/, /hazard.?tags?/]],
    ['start_time', [/start.?time/, /^start$/, /\bbegin\b/]],
    ['duration_minutes', [/duration/, /^minutes$/, /^hours$/, /^mins?$/, /elapsed/]],
    ['crew_size', [/crew.?size/, /^crew$/, /headcount/, /\bpersonnel\b/]],
    ['requires_jsa', [/jsa.?required/, /requires.?jsa/, /^jsa$/, /needs.?jsa/]],
    ['is_complete', [/^complete$/, /^completed$/, /^done$/, /is.?complete/]],
    ['completion_notes', [/completion.?notes?/, /^notes?$/, /^comments?$/]],
  ];

  for (const col of cols) {
    const n = normalizeHeader(col);
    for (const [field, regs] of patterns) {
      if (taken.has(field)) continue;
      if (regs.some((r) => r.test(n))) {
        out[col] = field;
        taken.add(field);
        break;
      }
    }
  }
  return out;
}

export function identityMappingFromMozaicHeaders(
  cols: string[]
): Record<string, ActivityField> {
  const out: Record<string, ActivityField> = {};
  const known = new Set<string>(ALL_FIELDS.map((f) => f.key));
  for (const col of cols) {
    const n = normalizeHeader(col);
    if (known.has(n)) out[col] = n as ActivityField;
  }
  return out;
}

function parseBool(raw: unknown): boolean {
  const s = String(raw ?? '').trim().toLowerCase();
  return ['true', '1', 'yes', 'y', 't'].includes(s);
}

function parseList(raw: unknown): string[] {
  if (raw == null || raw === '') return [];
  if (Array.isArray(raw)) return raw.map((v) => String(v).trim()).filter(Boolean);
  return String(raw)
    .split(/[|,;]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function normalizeStartTime(raw: unknown): string | null {
  const s = String(raw ?? '').trim();
  if (!s) return null;
  const m = s.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (!m) return null;
  const hh = m[1].padStart(2, '0');
  const today = new Date().toISOString().slice(0, 10);
  return `${today}T${hh}:${m[2]}:00`;
}

export interface ParsedActivity {
  description: string;
  status: ActivityStatus;
  risk_level: ActivityRiskLevel;
  risk_factor_category: string[];
  risk_tags: string[];
  start_time: string | null;
  duration_minutes: number | null;
  crew_size: number | null;
  is_jsa_required: boolean;
  is_complete: boolean;
  notes: string | null;
}

export function applyMappingToRows(
  rawRows: Record<string, unknown>[],
  columnMap: Record<string, ActivityField>
): { rows: ParsedActivity[]; errors: string[] } {
  const errors: string[] = [];
  const rows: ParsedActivity[] = [];

  const field2col: Partial<Record<ActivityField, string>> = {};
  for (const [col, field] of Object.entries(columnMap)) field2col[field] = col;

  rawRows.forEach((r, i) => {
    const rowNum = i + 2;
    const pick = (f: ActivityField): unknown => {
      const col = field2col[f];
      return col ? r[col] : undefined;
    };
    const str = (f: ActivityField) => String(pick(f) ?? '').trim();

    const description = str('description');
    if (!description) {
      errors.push(`Row ${rowNum}: missing description`);
      return;
    }

    const statusRaw = normalizeHeader(str('status') || 'planned');
    const status = (VALID_STATUSES as readonly string[]).includes(statusRaw)
      ? (statusRaw as ActivityStatus)
      : 'planned';

    const riskRaw = normalizeHeader(str('risk_level') || 'low');
    const risk = (VALID_RISK as readonly string[]).includes(riskRaw)
      ? (riskRaw as ActivityRiskLevel)
      : 'low';

    const cats = parseList(pick('risk_factor_category'))
      .map((s) => normalizeHeader(s))
      .filter((s) => (VALID_CATEGORIES as readonly string[]).includes(s));

    const tags = parseList(pick('risk_tags')).map((t) =>
      t.startsWith('#') ? t : `#${t}`
    );

    const durRaw = str('duration_minutes');
    const duration = durRaw && /^\d+(\.\d+)?$/.test(durRaw)
      ? Math.round(parseFloat(durRaw))
      : null;

    const crewRaw = str('crew_size');
    const crew = crewRaw && /^\d+$/.test(crewRaw) ? parseInt(crewRaw, 10) : null;

    const completionNotes = str('completion_notes');

    rows.push({
      description,
      status,
      risk_level: risk,
      risk_factor_category: cats,
      risk_tags: tags,
      start_time: normalizeStartTime(pick('start_time')),
      duration_minutes: duration,
      crew_size: crew,
      is_jsa_required: parseBool(pick('requires_jsa')),
      is_complete: parseBool(pick('is_complete')),
      notes: completionNotes || null,
    });
  });

  return { rows, errors };
}

// ─── Saved column mappings (module = 'activities') ──────────────────────
//
// Reuses the pob_column_mappings table with module = 'activities'.
// See migration 20260415000004_pob_column_mappings_module.sql.

export interface ActivityColumnMapping {
  id: string;
  tenant_id: string;
  mapping_name: string;
  column_map: Record<string, ActivityField>;
  is_default: boolean;
  module: 'activities';
  created_at: string;
  updated_at: string;
}

export async function getDefaultActivityMapping(): Promise<ActivityColumnMapping | null> {
  const { data, error } = await supabase
    .from('pob_column_mappings')
    .select('*')
    .eq('module', 'activities')
    .eq('is_default', true)
    .maybeSingle();
  if (error) return null;
  return (data ?? null) as ActivityColumnMapping | null;
}

export async function saveActivityMapping(params: {
  name: string;
  columnMap: Record<string, ActivityField>;
  makeDefault?: boolean;
}): Promise<ActivityColumnMapping> {
  const tenantId = await getTenantId();
  const createdBy = await getAuthUserId();

  if (params.makeDefault) {
    await supabase
      .from('pob_column_mappings')
      .update({ is_default: false, updated_at: new Date().toISOString() })
      .eq('tenant_id', tenantId)
      .eq('module', 'activities')
      .eq('is_default', true);
  }

  const { data, error } = await supabase
    .from('pob_column_mappings')
    .insert({
      tenant_id: tenantId,
      mapping_name: params.name,
      column_map: params.columnMap,
      is_default: !!params.makeDefault,
      module: 'activities',
      created_by: createdBy,
    })
    .select('*')
    .single();
  if (error) throw new Error(error.message);
  return data as ActivityColumnMapping;
}

export async function bulkInsertActivities(
  dsrId: string,
  activities: ParsedActivity[],
  tenantId: string,
  startSequence: number
): Promise<{ inserted: number; errors: string[] }> {
  if (activities.length === 0) return { inserted: 0, errors: [] };
  const userId = await getAuthUserId();
  const payload = activities.map((a, i) => ({
    dsr_report_id: dsrId,
    tenant_id: tenantId,
    created_by: userId,
    sequence_order: startSequence + i,
    description: a.description,
    status: a.status,
    risk_level: a.risk_level,
    risk_tags: a.risk_tags,
    risk_factor_category: a.risk_factor_category,
    start_time: a.start_time,
    duration_minutes: a.duration_minutes,
    crew_size: a.crew_size,
    is_jsa_required: a.is_jsa_required,
    is_complete: a.is_complete,
    notes: a.notes,
  }));
  const { error, count } = await supabase
    .from('dsr_activities')
    .insert(payload, { count: 'exact' });
  if (error) return { inserted: 0, errors: [error.message] };
  return { inserted: count ?? activities.length, errors: [] };
}
